import { useCallback, type CSSProperties } from "react";
import { useUi, useUiDispatch } from "@/app/AppContext";
import { DETAILS_LAYOUT } from "@/app/uiState";
import { useResizablePanel } from "@/components/useResizablePanel";
import { DetailsPanel } from "@/features/details/DetailsPanel";
import { Legend } from "./Legend";
import { Matrix } from "./Matrix";
import { useMatrixModel } from "./useMatrixModel";

export function MatrixTab() {
  const ui = useUi();
  const dispatch = useUiDispatch();
  const model = useMatrixModel();
  const panelVisible = ui.panel.kind !== "none";
  const isRight = ui.detailsPosition === "right";
  const commitWidth = useCallback(
    (width: number) => dispatch({ type: "setDetailsWidth", width }),
    [dispatch],
  );
  const { containerRef, panelWidth, splitterProps } = useResizablePanel({
    width: ui.detailsWidth,
    minPanelWidth: DETAILS_LAYOUT.minDetailsWidth,
    minMainWidth: DETAILS_LAYOUT.minMatrixWidth,
    enabled: isRight && panelVisible,
    onCommit: commitWidth,
  });

  const classes = [
    "matrix-layout",
    isRight ? "details-layout-right" : "details-layout-below",
    panelVisible ? "" : "details-panel-hidden",
    ui.editOnly && panelVisible ? "details-only-mode" : "",
  ].filter(Boolean);

  return (
    <div
      ref={containerRef}
      id="matrix-layout"
      className={classes.join(" ")}
      style={{ "--details-panel-width": `${Math.round(panelWidth)}px` } as CSSProperties}
    >
      <div id="matrix-left-column" className="matrix-left-column">
        <Matrix model={model} />
        <Legend model={model} />
      </div>
      <div {...splitterProps} id="matrix-splitter" aria-label="Resize details panel" />
      {panelVisible && (
        <div id="info-box" className="info-box">
          <DetailsPanel />
        </div>
      )}
    </div>
  );
}
